import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import SectionHeading from "../components/SectionHeading";
import { IMG, PACK_BAG } from "../lib/media";

const PARTNERS = [
  {
    name: "Bright Labs",
    role: "Website & Google Presence Partner",
    url: "https://www.brightlabs.live",
    logo: "/brand/brightlabs-lockup.png",
  },
];

export default function About() {
  const ref = useReveal();
  useSEO({
    title: "About — SugaSpice Bakery & Kitchen",
    description: "The story behind SugaSpice Bakery & Kitchen — combining bakery craft with bold takeaway food in St Neots, Cambridgeshire.",
    image: IMG.interior_main,
  });

  return (
    <div ref={ref}>
      <section className="pt-12 md:pt-16 pb-16 md:pb-20 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 fade-up">
              <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C8102E]">Our story</span>
              <h1 className="mt-5 font-display text-[clamp(2.6rem,7vw,6.5rem)] leading-[0.9] tracking-tight uppercase">
                A bakery built <br /><span className="text-[#C8102E]">to do more.</span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 fade-up space-y-6 text-lg md:text-xl text-[#2a2a2a] leading-relaxed">
              <p>
                SugaSpice started with a simple question: <em>why does a bakery have to mean just bread?</em> We grew up loving proper bakeries — fresh crusty bread with salted butter, rolls loaded with ham, cheese and pickle — and we grew up loving great takeaway food too. Smash burgers. Crispy fried chicken. Bold Indian flavours. Coffee that earns its rent.
              </p>
              <p>
                We didn't want to pick one. So we didn't.
              </p>
              <p>
                SugaSpice Bakery & Kitchen is what happens when a real bakery is built for the way people actually eat now. Great breakfasts. A proper lunch. Fresh bread to go with tonight's dinner. A cheeky smash burger on the way home. All from one spot, all fresh, all in St Neots.
              </p>
              <p>
                Alongside our shop, we supply local businesses across the region — petrol stations, convenience stores, cafés and independents — with fresh bakery and grab-and-go food. A flexible, relationship-led wholesale partnership, not a corporate supply chain.
              </p>
              <p>
                We're new, we're hungry, and we're just getting started.
              </p>
            </div>

            <div className="lg:col-span-5 grid grid-cols-2 gap-4 fade-up">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden">
                <img loading="lazy" decoding="async" src={IMG.interior_display} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] rounded-3xl overflow-hidden mt-10">
                <img loading="lazy" decoding="async" src={IMG.pastries_plate} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] rounded-3xl overflow-hidden -mt-6">
                <img loading="lazy" decoding="async" src={IMG.burger_dark} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] rounded-3xl overflow-hidden mt-4">
                <img loading="lazy" decoding="async" src={PACK_BAG} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F7F5F0] border-t border-[#E0D9CC]" data-testid="partners-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="Thank you" title="Our partners" />
          <p className="mt-5 max-w-2xl text-base md:text-lg text-[#3a3a3a] leading-relaxed">
            A huge thank you to the people and businesses helping us build SugaSpice. We're proud to work with them.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PARTNERS.map((p) => {
              const initials = p.name
                .split(" ")
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase();
              const slug = p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <a
                  key={p.name}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`partner-${slug}`}
                  className="group bg-white rounded-3xl border border-[#E8E3DB] p-7 flex flex-col gap-5 transition-all duration-300 hover:border-[#C8102E] hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(200,16,46,0.45)]"
                >
                  {p.logo ? (
                    <img
                      src={p.logo}
                      alt={p.name}
                      className="h-11 w-auto max-w-[200px] object-contain self-start"
                    />
                  ) : (
                    <div className="flex items-center gap-4">
                      <span className="h-14 w-14 rounded-2xl bg-[#1A1A1A] text-[#F7F5F0] font-display text-lg tracking-tight flex items-center justify-center shrink-0 group-hover:bg-[#C8102E] transition-colors">
                        {initials}
                      </span>
                      <h4 className="font-display text-xl uppercase tracking-tight leading-tight">{p.name}</h4>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-[#5C5C5C]">{p.role}</p>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-[#C8102E]">
                      Visit site <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#1A1A1A] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="What we stand for" title="Quality. Flavour. Community." invert />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { t: "Quality without the snobbery", b: "Proper craft technique, accessible prices, no airs and graces." },
              { t: "Flavour-first, always", b: "Big, bold, deeply seasoned. We don't do beige food." },
              { t: "Local first, ambitious second", b: "Built in St Neots. Designed to grow without losing the magic." },
            ].map((v, i) => (
              <div key={v.t} className="fade-up bg-[#262626] rounded-3xl p-7" style={{ transitionDelay: `${i * 60}ms` }}>
                <h4 className="font-display text-2xl uppercase tracking-tight">{v.t}</h4>
                <p className="mt-3 text-[#C9C5BE]">{v.b}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <Link to="/visit" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]">
              Visit the Bakery <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
